import { HttpClient, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { catchError } from "rxjs/operators";
import { APP_CONFIG } from "../lib/angular/injection-tokens";
import { IAppConfig } from "../lib/config/app-config";
import { LearningPlan } from "../_models/LearningPlan";
import { BaseService } from "./base-service";
import { AppConstants } from "../constants/constants";
import { LearningPlanEdit, LearningPlanEditByCategoryResult } from "../_models/LearningPlanEdit";
import { ManualLearningPlanResultDto } from "../_models/ManualLearningPlanResultDto";
import { SaveManualLearningPlanDto } from "../_models/SaveManualLearningPlanDto";
import { ExternalInitialAssessment } from "../_models/ExternalInitialAssessmentDto";
import { DigLitSelfAssessmentResult } from "../_models/DigitalLiteracy";

@Injectable({
  providedIn: "root",
})
export class LearningPlanService extends BaseService {
  private baseUrl: string = this.config.protectedResourceEndpoint;
  private static readonly path: string = "LearningPlan/";
  private static readonly VIEW_LEARNER_LEARNINGPLAN: string = "learner/view";
  private static readonly EDIT_LEARNER_LEARNINGPLAN: string = "learner/edit";
  private static readonly UPDATE_LEARNER_LEARNINGPLAN_FROM_PREENTRY: string = "learner/updateFromPreEntry";
  private static readonly VIEW_LEARNER_MANUALEDIT: string = "learner/viewmanualedit";
  private static readonly SAVE_LEARNER_MANUALEDIT: string = "learner/savemanualedit";
  private static readonly CREATE_EXT_INIT_ASSESSMENT: string = "learner/createexternalinitialassessment";
  private static readonly GET_LEARNER_DIG_LIT_SELF_ASSESSMENT: string = "learner/getdiglitselfassessment";
  private static readonly SAVE_LEARNER_DIG_LIT_SELF_ASSESSMENT: string = "learner/savediglitselfassessment";
  private static readonly SAVE_LEARNER_DIG_LIT_SELF_ASSESSMENT_SESSION: string = "learner/savediglitselfassessmentsession";
  private static readonly GET_CATEGORY_ON_CURRENT_LEVEL_CHANGE: string = "tutor/getCategoryOnCurrentLevelChange";

  constructor(http: HttpClient, @Inject(APP_CONFIG) private config: IAppConfig) {
    super(http);
  }

  public getLearningPlanByCourseId$(id): Observable<LearningPlan[]> {
    return this.http
      .get<LearningPlan[]>(this.baseUrl + LearningPlanService.path + id)
      .pipe(catchError(this.handleError));
  }

  public getLearnerLearningPlan$(learnerId: number, courseId: number): Observable<LearningPlan[]> {

    let params = new HttpParams();
    params = params.append(AppConstants.LEARNER_ID, String(learnerId));
    params = params.append(AppConstants.COURSE_ID, String(courseId));

    return this.http
      .get<LearningPlan[]>(`${this.baseUrl}${LearningPlanService.path}${LearningPlanService.VIEW_LEARNER_LEARNINGPLAN}`, {params});
  }
  
  public editLearningPlan$(editLearningPlan: LearningPlanEdit): Observable<boolean> {
    return this.http.put<boolean>(
      `${this.baseUrl}${LearningPlanService.path}${LearningPlanService.EDIT_LEARNER_LEARNINGPLAN}${AppConstants.SLASH}${editLearningPlan.learnerId}`, editLearningPlan);
  }

  public updateFromPreEntry$(editLearningPlan: LearningPlanEdit): Observable<boolean> {
    return this.http.put<boolean>(
      `${this.baseUrl}${LearningPlanService.path}${LearningPlanService.UPDATE_LEARNER_LEARNINGPLAN_FROM_PREENTRY}${AppConstants.SLASH}${editLearningPlan.learnerId}`, editLearningPlan);
  }

  public getLearnerManualEditLearningPlan$(learnerId: number, courseId: number): Observable<ManualLearningPlanResultDto[]> {

    let params = new HttpParams();
    params = params.append(AppConstants.LEARNER_ID, String(learnerId));
    params = params.append(AppConstants.COURSE_ID, String(courseId));

    return this.http
      .get<ManualLearningPlanResultDto[]>(`${this.baseUrl}${LearningPlanService.path}${LearningPlanService.VIEW_LEARNER_MANUALEDIT}`, {params});
  }

  public saveLearnerManualEditLearningPlan$(param: SaveManualLearningPlanDto): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.baseUrl}${LearningPlanService.path}${LearningPlanService.SAVE_LEARNER_MANUALEDIT}`, param);
  }

  public createExternalInitialAssessment$(extAssessment: ExternalInitialAssessment): Observable<ExternalInitialAssessment> {
    return this.http.post<ExternalInitialAssessment>(
      this.baseUrl + 
      LearningPlanService.path + 
      LearningPlanService.CREATE_EXT_INIT_ASSESSMENT, 
      extAssessment
    );
  }
  
  public getDigLitSelfAssessment$(learnerId: number): Observable<DigLitSelfAssessmentResult> {
    return this.http.get<DigLitSelfAssessmentResult>
      (`${this.baseUrl}${LearningPlanService.path}${LearningPlanService.GET_LEARNER_DIG_LIT_SELF_ASSESSMENT}${AppConstants.SLASH}${learnerId}`);
  }
  
  public saveDigLitSelfAssessment$(data: DigLitSelfAssessmentResult): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.baseUrl}${LearningPlanService.path}${LearningPlanService.SAVE_LEARNER_DIG_LIT_SELF_ASSESSMENT}`, data);
  }
  
  public saveDigLitSelfAssessmentSession$(data: DigLitSelfAssessmentResult): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.baseUrl}${LearningPlanService.path}${LearningPlanService.SAVE_LEARNER_DIG_LIT_SELF_ASSESSMENT_SESSION}`, data);
  }

  public getCategoryOnCurrentLevelChange$(topicId: number, courseId: number, categoryCode: string, level: string, learnerId: number): Observable<LearningPlanEditByCategoryResult> {

    let params = new HttpParams();
    params = params.append(AppConstants.TOPIC_ID, String(topicId));
    params = params.append(AppConstants.COURSE_ID, String(courseId));
    params = params.append(AppConstants.CATEGORY_CODE, String(categoryCode));
    params = params.append(AppConstants.LEVEL, String(level));
    params = params.append(AppConstants.LEARNER_ID, String(learnerId));
    return this.http
      .get<LearningPlanEditByCategoryResult>(`${this.baseUrl}${LearningPlanService.path}${LearningPlanService.GET_CATEGORY_ON_CURRENT_LEVEL_CHANGE}`, {params});
  }
}
